import type { DataFunctionArgs } from '@remix-run/node';
import { json } from '@remix-run/node';
import { getDirectoryPageMeta } from '@modules/category/api';
import { useLoaderData } from '@remix-run/react';
import PageLayout from '~/modules/design-board/component/page-layout/PageLayout';
import {
    ROLE_TYPE_PARAM_NAME,
    BOARD_TILE_LIMIT_PER_PAGE as defaultPageSize,
} from '~/modules/design-board/config/design-board-config';
import BoardList from '~/modules/design-board/component/board-list/BoardList';
import type {
    BoardListWithImageOnlyPinsFragment,
    DesignBoardRole,
    MemberBoardListRoleCountsQuery,
    MemberDesignBoardListQuery,
} from 'generated/graphql';
import { MemberBoardListRoleCountsDocument } from 'generated/graphql';
import { MemberDesignBoardListDocument } from 'generated/graphql';
import { getPaginationOpts } from '~/modules/root/util/get-pagination-opts.server';
import { assertResponse } from '~/modules/root/graphql/assertResponse.server';
import {
    mapMemberBoardListPinData,
    getValidEdgeNodes,
} from '~/modules/design-board/util';
import { pluralize } from '~/utils';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import {
    getCanonicalHreflang,
    createMetaTitle,
    createOGTags,
    mergeMeta,
} from '~/modules/seo';
import type { ActionFunctionArgs } from '@remix-run/node';
import { getActionResolver } from '@modules/design-board/actions';
import { useConfigContext } from '@archipro-website/config/bindings/react';
import type { APHandle } from '~/modules/root';
import { mapMemberBoardRoleCountData } from '~/modules/design-board/util/map-member-role-count-data.server';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const title = createMetaTitle(props, [`Design Boards`]);

        return [
            { title },
            ...createOGTags({
                meta: props,
                title,
            }),
            ...getCanonicalHreflang(props),
        ];
    }
);

export const action = async (args: ActionFunctionArgs) => {
    const request = args.request.clone();
    const url = new URL(args.request.url);
    const formData = await args.request.formData();

    const handler = getActionResolver(formData, url.searchParams);

    return handler(args.context, request, url);
};

export const handle: APHandle = {
    isBoardListPage: true,
};

export const loader = async (args: DataFunctionArgs) => {
    const { request, context, params } = args;
    const { device } = context;
    const url = new URL(request.url);
    const projectUrlSegment = params['projectUrlSegment'];
    const roleType = url.searchParams.get(
        ROLE_TYPE_PARAM_NAME
    ) as DesignBoardRole;

    const pageSettings = getPaginationOpts({
        request,
        device,
        defaultPageSize,
    });

    const [directoryPageMeta, boardList, roleCountResult] = await Promise.all([
        getDirectoryPageMeta({
            url,
            ...context,
        }),
        context.client.query<MemberDesignBoardListQuery>({
            query: MemberDesignBoardListDocument,
            variables: {
                limit: pageSettings.limit,
                offset: pageSettings.serverOffset,
                filters: { ByRole: roleType },
            },
        }),
        context.client.query<MemberBoardListRoleCountsQuery>({
            query: MemberBoardListRoleCountsDocument,
        }),
    ]);

    const rawBoardListEdges = await assertResponse(
        context,
        boardList.data?.getMember.DesignBoards?.edges,
        boardList.error
    );

    const validBoardListEdges =
        getValidEdgeNodes<BoardListWithImageOnlyPinsFragment[]>(
            rawBoardListEdges
        );

    const pagination = getPaginationOpts({
        request,
        device,
        defaultPageSize,
        totalCount:
            boardList.data?.getMember.DesignBoards?.pageInfo.totalCount || 0,
    });

    return json({
        deviceType: device.type,
        directoryPageMeta,
        boardListData: validBoardListEdges,
        pagination,
        roleCounts: mapMemberBoardRoleCountData(
            roleCountResult.data?.getMember
        ),
        projectUrlSegment: projectUrlSegment || '',
    });
};

const DesignBoardListPage = () => {
    const { boardListData, pagination } = useLoaderData<typeof loader>();
    const config = useConfigContext();

    const previewBoardListData = mapMemberBoardListPinData(
        config.assetsBaseUrl,
        boardListData
    );
    const { desktop } = useAppDisplayModeContext();

    return (
        <PageLayout
            mainTitle="DESIGN BOARDS"
            subTitle={
                desktop
                    ? pluralize(pagination.totalCount, 'Board')
                    : `${pagination.totalCount}`
            }
        >
            <BoardList
                boardListData={boardListData}
                previewListData={previewBoardListData}
                pagination={pagination}
            />
        </PageLayout>
    );
};

export default DesignBoardListPage;
